'use client';

import * as React from 'react';
import { useState } from 'react';
import type { IconSvgElement } from '@hugeicons/react';

import { cn } from '../../lib/utils';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
} from '../ui/input-group';
import { Field, FieldContent, FieldDescription, FieldError, FieldLabel, FieldTitle } from '../ui/field';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Switch } from '../ui/switch';
import type { InputProps } from '../ui/input';
import type { TextareaProps } from '../ui/textarea';

export type IconInputProps = Omit<InputProps, 'placeholder'> & {
  icon?: IconSvgElement;
  prefix?: React.ReactNode;
  trailing?: React.ReactNode;
  placeholder: string;
  description?: string;
  error?: string;
  groupClassName?: string;
};

export function IconInput({
  icon,
  prefix,
  trailing,
  placeholder,
  description,
  error,
  groupClassName,
  className,
  id,
  inputSize = 'lg',
  'aria-label': ariaLabel,
  ...props
}: IconInputProps) {
  return (
    <div className="space-y-1.5">
      <InputGroup className={groupClassName}>
        {icon ? (
          <InputGroupAddon align="inline-start">
            <HugeiconsIcon icon={icon} size={18} strokeWidth={1.75} className="shrink-0 text-muted-foreground" />
          </InputGroupAddon>
        ) : null}
        {prefix ? (
          <InputGroupAddon align="inline-start">
            <InputGroupText>{prefix}</InputGroupText>
          </InputGroupAddon>
        ) : null}
        <InputGroupInput
          id={id}
          placeholder={placeholder}
          aria-label={ariaLabel ?? placeholder}
          aria-invalid={error ? true : undefined}
          className={className}
          inputSize={inputSize}
          {...props}
        />
        {trailing ? <InputGroupAddon align="inline-end">{trailing}</InputGroupAddon> : null}
      </InputGroup>
      {description && !error ? <p className="text-caption text-muted-foreground">{description}</p> : null}
      {error ? <FieldError>{error}</FieldError> : null}
    </div>
  );
}

export type PasswordInputProps = Omit<IconInputProps, 'type' | 'trailing' | 'icon'> & {
  icon?: IconSvgElement;
  showLabel?: string;
  hideLabel?: string;
};

export function PasswordInput({
  icon,
  showLabel = 'Show',
  hideLabel = 'Hide',
  ...props
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);

  return (
    <IconInput
      {...props}
      icon={icon}
      type={visible ? 'text' : 'password'}
      trailing={
        <InputGroupButton
          type="button"
          variant="link"
          size="sm"
          className="h-8 shrink-0 px-2 text-caption font-medium text-muted-foreground no-underline hover:bg-transparent hover:text-foreground hover:no-underline"
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? hideLabel : showLabel}
        </InputGroupButton>
      }
    />
  );
}

export type LabeledTextareaProps = TextareaProps & {
  label: string;
  footer?: React.ReactNode;
  description?: string;
  error?: string;
  groupClassName?: string;
};

export function LabeledTextarea({
  label,
  footer,
  description,
  error,
  groupClassName,
  className,
  id,
  placeholder,
  ...props
}: LabeledTextareaProps) {
  return (
    <div className="space-y-1.5">
      <InputGroup className={groupClassName}>
        <InputGroupAddon align="block-start">{label}</InputGroupAddon>
        <InputGroupTextarea
          id={id}
          placeholder={placeholder ?? label}
          aria-label={label}
          aria-invalid={error ? true : undefined}
          className={className}
          {...props}
        />
        {footer ? <InputGroupAddon align="block-end">{footer}</InputGroupAddon> : null}
      </InputGroup>
      {description && !error ? <p className="text-caption text-muted-foreground">{description}</p> : null}
      {error ? <FieldError>{error}</FieldError> : null}
    </div>
  );
}

export type SettingToggleRowProps = {
  id?: string;
  label: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
};

export function SettingToggleRow({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
  className,
}: SettingToggleRowProps) {
  const generatedId = React.useId();
  const switchId = id ?? generatedId;

  return (
    <Field orientation="horizontal" className={cn('items-start justify-between gap-4 py-3', className)}>
      <FieldContent className="min-w-0">
        <FieldTitle>
          <label htmlFor={switchId} className="cursor-pointer">
            {label}
          </label>
        </FieldTitle>
        {description ? <FieldDescription>{description}</FieldDescription> : null}
      </FieldContent>
      <Switch
        id={switchId}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-label={label}
      />
    </Field>
  );
}

export type ChoiceCardOption<T extends string = string> = {
  value: T;
  label: string;
  description?: string;
};

export type ChoiceCardRadioGroupProps<T extends string = string> = {
  value: T;
  onValueChange: (value: T) => void;
  options: ChoiceCardOption<T>[];
  className?: string;
  name?: string;
};

export function ChoiceCardRadioGroup<T extends string = string>({
  value,
  onValueChange,
  options,
  className,
  name,
}: ChoiceCardRadioGroupProps<T>) {
  return (
    <RadioGroup
      value={value}
      onValueChange={(next) => onValueChange(next as T)}
      className={cn('gap-2', className)}
      name={name}
    >
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <FieldLabel
            key={option.value}
            htmlFor={`${name ?? 'choice'}-${option.value}`}
            className={cn(
              'w-full cursor-pointer rounded-lg border border-border p-4 transition-colors',
              selected ? 'border-foreground bg-secondary/50' : 'hover:bg-secondary/30'
            )}
          >
            <Field orientation="horizontal" className="items-start justify-between gap-4">
              <FieldContent>
                <FieldTitle>{option.label}</FieldTitle>
                {option.description ? <FieldDescription>{option.description}</FieldDescription> : null}
              </FieldContent>
              <RadioGroupItem value={option.value} id={`${name ?? 'choice'}-${option.value}`} />
            </Field>
          </FieldLabel>
        );
      })}
    </RadioGroup>
  );
}

export type SearchInputProps = Omit<IconInputProps, 'icon' | 'placeholder'> & {
  placeholder?: string;
};
